import { useState, useEffect, useCallback, useRef } from 'react';
import AccountLayout from '@/components/feature/AccountLayout';
import PortfolioPreviewModal from '@/components/common/PortfolioPreviewModal';
import Toast from '@/components/common/Toast';
import { useAuth } from '@/hooks/useAuth';
import {
  fetchGetProviderProfile,
  fetchSaveProviderProfileDraft,
  fetchSubmitProviderProfile,
  fetchOfflineProviderProfile,
  fetchRestoreProviderProfile,
} from '@/service/api/provider';
import { useBusinessCategoryStore } from '@/stores/businessCategoryStore';
import { useGeoStore } from '@/stores/geoStore';
import {
  ProviderProfileEditForm,
  ProviderProfileView,
  EditableFormData,
} from './modules';

export default function ProviderProfilePage() {
  const { user } = useAuth();

  const [profile, setProfile] = useState<Api.Provider.ProviderProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [tagInput, setTagInput] = useState('');

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setToast({ message, type });
  };

  // 代表作品预览弹窗状态
  const [previewWork, setPreviewWork] = useState<Api.Provider.ProviderPortfolioItemResponse | null>(null);

  // 表单状态
  const [formData, setFormData] = useState<EditableFormData>({});

  // 业务分类与地理位置 store
  const { categories, fetchCategories, getCategoryNameByCode } = useBusinessCategoryStore();
  const { provinces, fetchProvinces, getCityNameById } = useGeoStore();

  const hasFetchedRef = useRef(false);
  const categoriesFetchedRef = useRef(false);
  const provincesFetchedRef = useRef(false);

  useEffect(() => {
    if (!categoriesFetchedRef.current && categories.length === 0) {
      categoriesFetchedRef.current = true;
      fetchCategories().catch((err) => console.error('获取业务类目失败:', err));
    }
    if (!provincesFetchedRef.current && provinces.length === 0) {
      provincesFetchedRef.current = true;
      fetchProvinces().catch((err) => console.error('获取城市列表失败:', err));
    }
  }, [categories.length, fetchCategories, provinces.length, fetchProvinces]);

  const isProfileNotFoundErr = (err: any) => {
    if (!err) return false;
    const code = err.code ?? err.status ?? err.response?.data?.code;
    const errorKey = err.error_key ?? err.errorKey ?? err.response?.data?.error_key;
    const msg = err.message || (typeof err === 'string' ? err : '');

    return (
      code === 41033 ||
      code === 41028 ||
      errorKey === 'party.provider_profile_not_found' ||
      errorKey === 'party.market_residency_not_found' ||
      msg.includes('供给方档案不存在') ||
      msg.includes('party.provider_profile_not_found') ||
      msg.includes('市场入驻申请不存在') ||
      msg.includes('party.market_residency_not_found')
    );
  };

  const loadProfile = useCallback(
    async (force = false) => {
      if (hasFetchedRef.current && !force) return;
      hasFetchedRef.current = true;

      setLoading(true);
      try {
        const res = await fetchGetProviderProfile();
        setProfile(res || null);
      } catch (err: any) {
        console.error('获取人才档案失败:', err);
        if (!isProfileNotFoundErr(err)) {
          showToast(err instanceof Error ? err.message : '获取人才档案失败', 'error');
        }
        setProfile(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const initFormData = useCallback(() => {
    const target = profile?.draft_profile || profile;
    setFormData({
      avatar_asset_id: target?.avatar_asset_id || target?.avatar?.id || '',
      _tempAvatarUrl: target?.avatar?.access_url || '',
      display_name: target?.display_name || '',
      category_codes: target?.category_codes || [],
      service_mode: target?.service_mode || 'FULL_TIME',
      daily_rate: target?.daily_rate,
      currency: target?.currency || 'CNY',
      city_code: target?.city_code || '',
      remote: target?.remote ?? true,
      seeking_work: target?.seeking_work ?? true,
      skill_tags: target?.skill_tags || [],
      wechat_id: target?.wechat_id || target?.contact_wechat_id_masked || profile?.wechat_id || profile?.contact_wechat_id_masked || '',
      phone: target?.phone || target?.contact_phone_masked || profile?.phone || profile?.contact_phone_masked || '',
      phone_verification_token: target?.phone_verification_token || profile?.phone_verification_token || '',
      email: target?.email || target?.contact_email_masked || profile?.email || profile?.contact_email_masked || '',
      email_verification_token: target?.email_verification_token || profile?.email_verification_token || '',
      intro: target?.intro || '',
      service_scope: target?.service_scope || '',
      portfolio: target?.portfolio
        ? target.portfolio.map((p) => {
          const isVid =
            p.attachments?.category === 'PORTFOLIO_VIDEO' ||
            (p.attachments?.category || '').toUpperCase().includes('VIDEO');
          return {
            name: p.name || '',
            intro: p.intro || '',
            attachments: p.attachments
              ? {
                category: isVid ? 'PORTFOLIO_VIDEO' : 'PORTFOLIO_IMAGE',
                display_name: p.attachments.display_name || '',
                sort_no: p.attachments.sort_no || 0,
                upload_asset_id: p.attachments.upload_asset_id || p.attachments.id,
              }
              : undefined,
            _tempPreviewUrl: p.attachments?.access_url,
          };
        })
        : [],
    });
  }, [profile]);

  const handleStartEdit = () => {
    initFormData();
    setIsEditing(true);
  };

  const handleToggleCategory = (code: string) => {
    const current = formData.category_codes || [];
    if (current.includes(code)) {
      setFormData((prev) => ({
        ...prev,
        category_codes: (prev.category_codes || []).filter((c) => c !== code),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        category_codes: [...(prev.category_codes || []), code],
      }));
    }
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    const current = formData.skill_tags || [];
    if (trimmed && !current.includes(trimmed)) {
      setFormData((prev) => ({
        ...prev,
        skill_tags: [...current, trimmed],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      skill_tags: (prev.skill_tags || []).filter((t) => t !== tag),
    }));
  };

  // 作品集操作
  const handleAddPortfolioItem = () => {
    setFormData((prev) => ({
      ...prev,
      portfolio: [
        ...(prev.portfolio || []),
        {
          name: '',
          intro: '',
          attachments: {
            category: 'PORTFOLIO_IMAGE',
            sort_no: (prev.portfolio || []).length,
          },
        },
      ],
    }));
  };

  const handleRemovePortfolioItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      portfolio: (prev.portfolio || []).filter((_, i) => i !== index),
    }));
  };

  const handlePortfolioChange = (index: number, field: 'name' | 'intro', value: string) => {
    setFormData((prev) => {
      const list = [...(prev.portfolio || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, portfolio: list };
    });
  };

  const validateForm = (): boolean => {
    if (!formData.intro?.trim()) {
      showToast('简介不能为空', 'error');
      return false;
    }
    if (!formData.service_scope?.trim()) {
      showToast('服务范围不能为空', 'error');
      return false;
    }
    return true;
  };

  const handleSaveDraft = async () => {
    if (!validateForm()) return;

    setSavingDraft(true);
    try {
      const cleanedFormData: Api.Provider.SaveProviderProfileRequest = {
        ...formData,
        avatar_asset_id: formData.avatar_asset_id || undefined,
        email_verification_token: formData.email_verification_token || undefined,
        phone_verification_token: formData.phone_verification_token || undefined,
        portfolio: formData.portfolio
          ?.filter((p) => p.name.trim())
          .map((p, idx) => ({
            name: p.name.trim(),
            intro: p.intro,
            attachments: p.attachments
              ? {
                ...p.attachments,
                category: p.attachments.category || 'PORTFOLIO_IMAGE',
                display_name: p.attachments.display_name || p.name || '作品文件',
                sort_no: idx,
              }
              : undefined,
          })),
      };

      const updated = await fetchSaveProviderProfileDraft(cleanedFormData);
      setProfile(updated);
      showToast('档案草稿保存成功', 'success');
      setIsEditing(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '保存草稿失败', 'error');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmitProfile = async () => {
    if (isEditing && !validateForm()) return;

    setSubmitting(true);
    try {
      if (isEditing) {
        const cleanedFormData: Api.Provider.SaveProviderProfileRequest = {
          ...formData,
          avatar_asset_id: formData.avatar_asset_id || undefined,
          email_verification_token: formData.email_verification_token || undefined,
          phone_verification_token: formData.phone_verification_token || undefined,
          portfolio: formData.portfolio
            ?.filter((p) => p.name.trim())
            .map((p, idx) => ({
              name: p.name.trim(),
              intro: p.intro,
              attachments: p.attachments
                ? {
                  ...p.attachments,
                  category: p.attachments.category || 'PORTFOLIO_IMAGE',
                  display_name: p.attachments.display_name || p.name || '作品文件',
                  sort_no: idx,
                }
                : undefined,
            })),
        };
        await fetchSaveProviderProfileDraft(cleanedFormData);
      }
      const updated = await fetchSubmitProviderProfile();
      setProfile(updated);
      showToast('档案已成功提交审核', 'success');
      setIsEditing(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '提交审核失败', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOfflineProfile = async () => {
    setTogglingStatus(true);
    try {
      const res = await fetchOfflineProviderProfile();
      setProfile(res);
      showToast('已取消供给方大厅展示（已下架）', 'success');
    } catch (err: any) {
      showToast(err instanceof Error ? err.message : '取消大厅展示失败', 'error');
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleRestoreProfile = async () => {
    setTogglingStatus(true);
    try {
      const res = await fetchRestoreProviderProfile();
      setProfile(res);
      showToast('已恢复供给方大厅展示（已重新上线）', 'success');
    } catch (err: any) {
      showToast(err instanceof Error ? err.message : '恢复大厅展示失败', 'error');
    } finally {
      setTogglingStatus(false);
    }
  };

  const currentDisplay = profile?.published_profile || profile?.draft_profile || profile;

  const renderStatusBadge = () => {
    if (!profile) {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">未填写</span>;
    }
    const status = profile?.status || profile?.revision_status;
    if (status === 'APPROVED' || status === 'PUBLISHED') {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">已发布</span>;
    }
    if (status === 'OFFLINE') {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">已下架/隐蔽</span>;
    }
    if (status === 'PENDING' || status === 'AUDITING') {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">审核中</span>;
    }
    if (status === 'REJECTED') {
      return <span className="px-3 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700">审核未通过</span>;
    }
    return <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">草稿</span>;
  };

  return (
    <AccountLayout
      activePath="/provider-profile"
      breadcrumbs={[{ label: '账号中心', href: '/profile' }, { label: '我的档案' }]}
    >
      <div className="glass-card rounded-2xl p-6 md:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading font-bold text-lg text-foreground-950">我的档案</h1>
              {renderStatusBadge()}
            </div>
            <p className="text-xs text-foreground-500 mt-1">完善您的服务能力与擅长领域，以便需求方快速找到您</p>
          </div>
          {!loading && !isEditing && (
            <div className="flex items-center gap-3">
              <button
                onClick={handleStartEdit}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 transition-all cursor-pointer flex items-center gap-1.5"
              >
                <i className="ri-edit-line"></i>
                {profile ? '编辑档案' : '填写档案'}
              </button>

              {/* 下架与恢复大厅展示控制按钮 */}
              {profile && (profile.status === 'APPROVED' || profile.status === 'PUBLISHED') && (
                <button
                  onClick={handleOfflineProfile}
                  disabled={togglingStatus}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-100 transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1"
                >
                  <i className="ri-eye-off-line"></i>
                  {togglingStatus ? '处理中...' : '取消大厅展示'}
                </button>
              )}

              {profile && (profile.status === 'OFFLINE' || (profile.published_profile && profile.status !== 'APPROVED' && profile.status !== 'PUBLISHED')) && (
                <button
                  onClick={handleRestoreProfile}
                  disabled={togglingStatus}
                  className="px-4 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium hover:bg-emerald-100 transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1"
                >
                  <i className="ri-eye-line"></i>
                  {togglingStatus ? '处理中...' : '恢复大厅展示'}
                </button>
              )}

              {profile && profile.status !== 'PENDING' && (
                <button
                  onClick={handleSubmitProfile}
                  disabled={submitting}
                  className="px-4 py-2 rounded-lg border border-primary-300 text-primary-700 text-xs font-medium hover:bg-primary-50 transition-all cursor-pointer disabled:opacity-50"
                >
                  {submitting ? '提交中...' : '提交审核'}
                </button>
              )}
            </div>
          )}
        </div>

        {profile?.audit_remark && profile.status === 'REJECTED' && (
          <div className="mb-6 p-4 rounded-xl bg-rose-50 border border-rose-200/80 text-rose-700 text-xs">
            <p className="font-semibold mb-1">审核未通过原因：</p>
            <p>{profile.audit_remark}</p>
          </div>
        )}

        {loading ? (
          <div className="py-16 text-center text-foreground-400 text-sm">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent mb-3"></div>
            <p>加载人才档案中...</p>
          </div>
        ) : isEditing ? (
          <ProviderProfileEditForm
            formData={formData}
            setFormData={setFormData}
            categories={categories}
            currentDisplay={currentDisplay}
            tagInput={tagInput}
            setTagInput={setTagInput}
            handleAddTag={handleAddTag}
            handleRemoveTag={handleRemoveTag}
            handleToggleCategory={handleToggleCategory}
            handleAddPortfolioItem={handleAddPortfolioItem}
            handleRemovePortfolioItem={handleRemovePortfolioItem}
            handlePortfolioChange={handlePortfolioChange}
            handleSaveDraft={handleSaveDraft}
            handleSubmitProfile={handleSubmitProfile}
            onCancel={() => setIsEditing(false)}
            savingDraft={savingDraft}
            submitting={submitting}
            showToast={showToast}
          />
        ) : (
          <ProviderProfileView
            currentDisplay={currentDisplay}
            profile={profile}
            user={user}
            getCategoryNameByCode={getCategoryNameByCode}
            getCityNameById={getCityNameById}
            onPreviewWork={(work) => setPreviewWork(work)}
            onStartEdit={handleStartEdit}
          />
        )}
      </div>

      {/* 代表作品预览弹窗 */}
      <PortfolioPreviewModal
        open={Boolean(previewWork)}
        title={previewWork?.name}
        intro={previewWork?.intro}
        isVideo={
          previewWork?.attachments?.category === 'PORTFOLIO_VIDEO' ||
          (previewWork?.attachments?.category || '').toUpperCase().includes('VIDEO')
        }
        mediaUrl={previewWork?.attachments?.access_url}
        onClose={() => setPreviewWork(null)}
      />
      {/* Toast 提示消息 */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </AccountLayout>
  );
}
